import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  List,
  ListItem,
  ListItemText,
  Button,
  Alert,
  CircularProgress,
  Chip,
  Divider,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import DownloadIcon from '@mui/icons-material/Download';
import DescriptionIcon from '@mui/icons-material/Description';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import ScienceIcon from '@mui/icons-material/Science';
import { useTranslation } from 'react-i18next';
import { candidateAPI } from '../../utils/api';
import { formatToBogotaTime } from '../../utils/dateUtils';
import TestRecordingView from './TestRecordingView';

const API_BASE_URL = process.env.REACT_APP_API_URL || '/api';

const QuestionList = ({ assignment, onStartQuestion, onBack }) => {
  const { t } = useTranslation();
  const [questions, setQuestions] = useState([]);
  const [assessmentName, setAssessmentName] = useState('');
  const [assessmentDescription, setAssessmentDescription] = useState('');
  const [attachments, setAttachments] = useState([]);
  const [timePerQuestionMinutes, setTimePerQuestionMinutes] = useState(15);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showTestView, setShowTestView] = useState(false);

  useEffect(() => {
    loadQuestions();
  }, [assignment.id]);

  const loadQuestions = async () => {
    setLoading(true);
    setError('');

    try {
      const data = await candidateAPI.getAssignmentQuestions(assignment.id);
      setQuestions(data.questions);
      setAssessmentName(data.assessment_name);
      setAssessmentDescription(data.assessment_description);
      setAttachments(data.attachments || []);
      if (typeof data.time_per_question_minutes === 'number') {
        setTimePerQuestionMinutes(data.time_per_question_minutes);
      }
    } catch (err) {
      setError(err.message || 'Failed to load questions');
    } finally {
      setLoading(false);
    }
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return '';
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
        <CircularProgress />
      </Box>
    );
  }

  // If showing test view, render it instead of question list
  if (showTestView) {
    return (
      <TestRecordingView
        assignment={assignment}
        onBack={() => setShowTestView(false)}
      />
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h5">
          {assessmentName}
        </Typography>
        <Button variant="outlined" onClick={onBack}>
          {t('candidate.questions.backToAssignments')}
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* Assessment Info Section */}
      {(assessmentDescription || attachments.length > 0) && (
        <Paper sx={{ p: 3, mb: 3, backgroundColor: '#f5f9ff' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <DescriptionIcon color="primary" sx={{ mr: 1 }} />
            <Typography variant="h6">
              {t('candidate.questions.assessmentMaterials')}
            </Typography>
          </Box>

          {assessmentDescription && (
            <Typography variant="body1" sx={{ mb: 2 }}>
              {assessmentDescription}
            </Typography>
          )}

          {attachments.length > 0 && (
            <Box>
              <Typography variant="subtitle2" gutterBottom>
                {t('candidate.questions.downloadableFiles')}
              </Typography>
              <List dense>
                {attachments.map((attachment) => (
                  <ListItem
                    key={attachment.id}
                    sx={{
                      border: 1,
                      borderColor: 'divider',
                      borderRadius: 1,
                      mb: 1,
                      backgroundColor: 'white',
                    }}
                  >
                    <AttachFileIcon color="primary" sx={{ mr: 1 }} />
                    <ListItemText
                      primary={attachment.filename}
                      secondary={formatFileSize(attachment.file_size)}
                    />
                    <Button
                      variant="contained"
                      size="small"
                      startIcon={<DownloadIcon />}
                      onClick={() => window.open(`${API_BASE_URL}${attachment.url}`, '_blank')}
                    >
                      {t('common.download')}
                    </Button>
                  </ListItem>
                ))}
              </List>
            </Box>
          )}
        </Paper>
      )}

      {/* Test Your Setup Section */}
      <Paper sx={{ p: 3, mb: 3, backgroundColor: '#e8f5e9', borderLeft: '4px solid #4caf50' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <ScienceIcon color="success" sx={{ mr: 1, fontSize: 32 }} />
          <Typography variant="h6">
            {t('candidate.questions.testSetup.title')}
          </Typography>
        </Box>

        <Typography variant="body1" sx={{ mb: 2 }}>
          {t('candidate.questions.testSetup.description')}
        </Typography>

        <Button
          variant="contained"
          color="success"
          startIcon={<ScienceIcon />}
          onClick={() => setShowTestView(true)}
        >
          {t('candidate.questions.testSetup.startButton')}
        </Button>
      </Paper>

      <Paper sx={{ p: 3 }}>
        <Typography variant="h6" gutterBottom>
          {t('candidate.questions.title')}
        </Typography>

        <Alert severity="info" sx={{ mb: 3 }}>
          {t('candidate.questionView.screenShareRequired')}
        </Alert>

        <List>
          {questions.map((question) => (
            <ListItem
              key={question.question_id}
              sx={{
                border: 1,
                borderColor: 'divider',
                borderRadius: 1,
                mb: 2,
                backgroundColor: question.is_completed ? '#f0f9ff' : 'background.paper',
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  mr: 2,
                  color: question.is_completed ? 'success.main' : 'text.secondary',
                }}
              >
                {question.is_completed ? (
                  <CheckCircleIcon />
                ) : (
                  <RadioButtonUncheckedIcon />
                )}
              </Box>

              <ListItemText
                primary={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography variant="h6">
                      {t('candidate.questions.questionNumber', { number: question.order_number })}
                    </Typography>
                    {question.is_completed && (
                      <Chip
                        label={t('candidate.questions.completed')}
                        color="success"
                        size="small"
                      />
                    )}
                    {question.is_started && !question.is_completed && (
                      <Chip
                        label={t('candidate.questions.notCompleted')}
                        color="error"
                        size="small"
                      />
                    )}
                  </Box>
                }
                secondary={
                  question.is_completed
                    ? t('candidate.questions.completed')
                    : question.is_started
                    ? `Started: ${formatToBogotaTime(question.started_at)}`
                    : t('candidate.questions.startQuestion')
                }
              />

              <Button
                variant="contained"
                onClick={() => onStartQuestion(question, timePerQuestionMinutes)}
                disabled={question.is_completed || question.is_started}
                sx={{ ml: 2 }}
              >
                {question.is_completed ? t('candidate.questions.completed') : question.is_started ? t('candidate.questions.notCompleted') : t('candidate.questions.startQuestion')}
              </Button>
            </ListItem>
          ))}
        </List>
      </Paper>
    </Box>
  );
};

export default QuestionList;
